import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { getAdminOrders } from "../../api/adminApi";

export default function AdminOrders() {
  const history = useHistory();

  // Filters
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");

  // Data state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);

  // Debounce search typing (wait 350ms after you stop typing)
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Build query params to send to backend
  const params = useMemo(() => {
    const p = {};
    if (status) p.status = status;
    if (q) p.q = q;
    return p;
  }, [status, q]);

  // Load orders from backend
  useEffect(() => {
    let isMounted = true;

    async function loadOrders() {
      setLoading(true);
      setError("");

      try {
        const res = await getAdminOrders(params);

        // Accept different backend response shapes:
        // - res.data.items
        // - res.data.orders
        // - res.data (array)
        const data = res?.data;
        const list =
          Array.isArray(data) ? data : data?.items || data?.orders || [];

        if (isMounted) setOrders(list);
      } catch (e) {
        if (!isMounted) return;

        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load orders.";

        setError(msg);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadOrders();
    return () => {
      isMounted = false;
    };
  }, [params]);

  // Helpers to handle snake_case vs camelCase
  const getId = (o) => o.id || o.order_id || o.orderId;
  const getStatus = (o) => o.status || o.order_status || o.orderStatus || "-";
  const getCreated = (o) => o.created_at || o.createdAt || null;
  const getCustomer = (o) =>
    o.customer_name ||
    o.customerName ||
    o.customer?.name ||
    o.customer ||
    "-";
  const getCourier = (o) =>
    o.courier_name || o.courierName || o.courier?.name || o.courier || "-";
  const getTotal = (o) =>
    o.total || o.amount || o.price || o.total_amount || "-";

  const openOrder = (order) => {
    const id = getId(order);
    if (!id) return;
    history.push(`/admin/orders/${id}`);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
        Orders
      </Typography>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="created">Created</MenuItem>
          <MenuItem value="accepted">Accepted</MenuItem>
          <MenuItem value="in_transit">In Transit</MenuItem>
          <MenuItem value="delivered">Delivered</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>

        <TextField
          label="Search (order/customer/courier)"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          size="small"
          sx={{ minWidth: 320 }}
        />
      </Box>

      {/* Loading + error */}
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography>Loading orders…</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Orders table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Courier</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography sx={{ py: 2 }}>No orders found.</Typography>
                </TableCell>
              </TableRow>
            )}

            {orders.map((o) => {
              const id = getId(o);
              return (
                <TableRow
                  key={id || JSON.stringify(o)}
                  hover
                  onClick={() => openOrder(o)}
                  sx={{ cursor: id ? "pointer" : "default" }}
                >
                  <TableCell>{id || "-"}</TableCell>
                  <TableCell>{getStatus(o)}</TableCell>
                  <TableCell>
                    {getCreated(o) ? new Date(getCreated(o)).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell>{getCustomer(o)}</TableCell>
                  <TableCell>{getCourier(o)}</TableCell>
                  <TableCell>{getTotal(o)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ mt: 2, opacity: 0.7 }}>
        Click any row to open Order Details.
      </Typography>
    </Box>
  );
}
